import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CopyPlus, CreditCard, Eye, EyeOff, GripVertical, Hash, ImagePlus, Link2, Loader2, MapPin, Network,
  PenLine, Phone, Plus, QrCode, Trash2,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { AdminCard, AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";
import ImageUpload from "@/components/admin/ImageUpload";

const EMPTY = {
  name: "", payment_type: "link", description: "", link: "", logo_url: "",
  payment_number: "", beneficiary_name: "",
  qr_image_url: "", address: "", network: "",
  convert_enabled: false, convert_currency: "", convert_rate: 0,
  instructions: "",
  require_txid: true, require_proof: false,
  show_number: true, show_address: true, show_network: true, show_qr: true,
  is_active: true, sort_order: 0,
};

const TYPES = [
  { value: "link", label: "Paiement par lien", desc: "Redirige le client vers un lien externe (carte, PayPal…)" },
  { value: "manual", label: "Paiement manuel avec numéro", desc: "Orange Money, Wave… le client paie sur un numéro" },
  { value: "qr", label: "Paiement par QR Code", desc: "Binance, USDT… QR code + adresse + réseau" },
  { value: "other", label: "Autre", desc: "Moyen personnalisé avec instructions" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const TYPE_TONE = { link: "blue", manual: "green", qr: "amber", other: "slate" };

const mediaSrc = (u) => (!u ? "" : u.startsWith("http") || u.startsWith("/api/") ? fileUrl(u) : u);

function Toggle({ checked, onChange, label, hint, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-200 ${
        checked ? "border-emerald-200 bg-emerald-50" : "border-line bg-white"
      }`}
    >
      <span>
        <span className={`block text-xs font-bold ${checked ? "text-emerald-700" : "text-slate-600"}`}>{label}</span>
        {hint && <span className="mt-0.5 block text-[11px] text-slate-500">{hint}</span>}
      </span>
      <span className={`h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors duration-200 ${checked ? "bg-emerald-500" : "bg-slate-300"}`}>
        <span className={`block h-4 w-4 rounded-full bg-white transition-transform duration-200 ${checked ? "translate-x-4" : ""}`} />
      </span>
    </button>
  );
}

export default function AdminPaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const logoRef = useRef(null);

  const load = () =>
    api.get("/admin/payment-methods").then(({ data }) => setMethods(data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (method) => { setEditing(method); setForm({ ...EMPTY, ...method }); setModalOpen(true); };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", data.url);
      toast.success("Logo ajouté");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploadingLogo(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("Le nom est requis");
    if (form.payment_type === "manual" && !form.payment_number.trim())
      return toast.error("Le numéro de paiement est requis");
    if (form.payment_type === "qr" && !form.address.trim())
      return toast.error("L'adresse de paiement est requise");
    if (form.payment_type === "qr" && !form.network.trim())
      return toast.error("Le réseau est requis");
    if (form.payment_type === "qr" && form.convert_enabled && Number(form.convert_rate) <= 0)
      return toast.error("Indiquez un taux de conversion valide (FCFA)");
    setSaving(true);
    try {
      const payload = { ...form, convert_rate: Number(form.convert_rate) || 0 };
      if (editing) {
        await api.put(`/admin/payment-methods/${editing.id}`, payload);
        toast.success("Moyen de paiement mis à jour");
      } else {
        await api.post("/admin/payment-methods", payload);
        toast.success("Moyen de paiement créé");
      }
      setModalOpen(false);
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (method) => {
    try {
      await api.put(`/admin/payment-methods/${method.id}`, { ...method, is_active: !method.is_active });
      toast.success(method.is_active ? `${method.name} masqué côté client` : `${method.name} activé`);
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const duplicate = async (method) => {
    try {
      await api.post(`/admin/payment-methods/${method.id}/duplicate`);
      toast.success("Moyen dupliqué (désactivé par défaut)");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const remove = async (method) => {
    if (!window.confirm(`Supprimer définitivement « ${method.name} » ?`)) return;
    try {
      await api.delete(`/admin/payment-methods/${method.id}`);
      toast.success("Moyen supprimé");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const persistOrder = async (next) => {
    setMethods(next);
    try {
      await api.post("/admin/payment-methods/reorder", { ids: next.map((m) => m.id) });
    } catch (error) {
      toast.error(apiError(error));
      load();
    }
  };

  const onDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return setDragIndex(null);
    const next = [...methods];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setDragIndex(null);
    persistOrder(next);
  };

  const bulk = useBulkDelete("/admin/payment-methods/bulk-delete", load, "moyen(s) de paiement");

  const detailLine = (m) => {
    if (m.payment_type === "manual") return `${m.payment_number || "—"}${m.beneficiary_name ? ` · ${m.beneficiary_name}` : ""}`;
    if (m.payment_type === "qr") return `${m.network || "—"} · ${(m.address || "").slice(0, 14)}${(m.address || "").length > 14 ? "…" : ""}`;
    if (m.payment_type === "link") return m.link || "Lien de paiement";
    return m.description || "—";
  };

  return (
    <div className="space-y-5">
      <AdminCard
        title="⚙️ Moyens de paiement"
        description="Créez et activez des moyens de paiement manuels (numéro), par QR code (crypto) ou par lien. Réorganisez-les par glisser-déposer."
        action={
          <button
            data-testid="pm-add-btn"
            onClick={openCreate}
            className="flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-xs font-bold text-white hover:bg-brand/90"
          >
            <Plus className="h-4 w-4" /> Ajouter un moyen
          </button>
        }
      >
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : methods.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-line p-10 text-center" data-testid="pm-empty">
            <CreditCard className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-bold text-ink">Aucun moyen de paiement configuré</p>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500">
              <SelectCheckbox testId="bulk-all-pm"
                checked={methods.length > 0 && bulk.selected.length >= methods.length}
                onChange={() => bulk.toggleAll(methods.map((m) => m.id))} />
              Tout sélectionner
            </div>
            <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-pm" />
            <div className="space-y-3" data-testid="pm-list">
              {methods.map((method, index) => (
                <div
                  key={method.id}
                  data-testid={`pm-row-${index}`}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(index)}
                  className={`flex flex-wrap items-center gap-3 rounded-2xl border p-4 transition-shadow ${
                    dragIndex === index ? "ring-2 ring-brand/40" : ""
                  } ${method.is_active ? "border-line bg-white" : "border-dashed border-line bg-slate-50 opacity-70"}`}
                >
                  <SelectCheckbox testId={`bulk-pm-${method.id}`}
                    checked={bulk.selected.includes(method.id)} onChange={() => bulk.toggle(method.id)} />
                  <span data-testid={`pm-drag-${index}`} className="cursor-grab text-slate-300 active:cursor-grabbing" title="Glisser pour réordonner">
                    <GripVertical className="h-5 w-5" />
                  </span>
                  {method.logo_url ? (
                    <img src={mediaSrc(method.logo_url)} alt={method.name} className="h-12 w-12 rounded-xl border border-line object-contain p-1" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-light text-brand">
                      {method.payment_type === "qr" ? <QrCode className="h-6 w-6" /> : <CreditCard className="h-6 w-6" />}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-extrabold text-ink">{method.name}</p>
                      <Pill tone={TYPE_TONE[method.payment_type] || "slate"}>{TYPE_LABEL[method.payment_type] || "—"}</Pill>
                      <Pill tone={method.is_active ? "green" : "slate"}>{method.is_active ? "Actif" : "Masqué"}</Pill>
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{detailLine(method)}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {method.require_txid && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">TXID requis</span>}
                      {method.require_proof && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">Preuve requise</span>}
                      {method.payment_type === "qr" && method.convert_enabled && (
                        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">1 {method.convert_currency} = {method.convert_rate} FCFA</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`pm-toggle-${index}`} onClick={() => toggleActive(method)} title={method.is_active ? "Masquer" : "Activer"}
                      className="rounded-lg border border-line p-2 text-slate-500 hover:border-brand hover:text-brand">
                      {method.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button data-testid={`pm-duplicate-${index}`} onClick={() => duplicate(method)} title="Dupliquer"
                      className="rounded-lg border border-line p-2 text-slate-500 hover:border-brand hover:text-brand">
                      <CopyPlus className="h-4 w-4" />
                    </button>
                    <button data-testid={`pm-edit-${index}`} onClick={() => openEdit(method)} title="Modifier"
                      className="rounded-lg border border-line p-2 text-slate-500 hover:border-brand hover:text-brand">
                      <PenLine className="h-4 w-4" />
                    </button>
                    <button data-testid={`pm-delete-${index}`} onClick={() => remove(method)} title="Supprimer"
                      className="rounded-lg border border-line p-2 text-slate-500 hover:border-rose-300 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </AdminCard>

      <AdminModal open={modalOpen} onOpenChange={setModalOpen} wide
        title={editing ? `Modifier « ${editing.name} »` : "Nouveau moyen de paiement"}>
        <div className="space-y-4">
          {/* Type de paiement */}
          <Field label="Type de paiement" hint="Les champs affichés changent selon le type sélectionné.">
            <div className="grid gap-2 sm:grid-cols-2" data-testid="pm-type-group">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  data-testid={`pm-type-${t.value}`}
                  onClick={() => set("payment_type", t.value)}
                  className={`rounded-xl border-2 p-3 text-left transition-colors duration-150 ${
                    form.payment_type === t.value ? "border-brand bg-brand-soft" : "border-line hover:border-brand/40"
                  }`}
                >
                  <span className="block text-xs font-extrabold text-ink">{t.label}</span>
                  <span className="mt-0.5 block text-[11px] text-slate-500">{t.desc}</span>
                </button>
              ))}
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nom du moyen de paiement">
              <input data-testid="pm-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Ex : Orange Money, Binance…" className={inputCls} />
            </Field>
            <Field label="Image / logo">
              <div className="flex items-center gap-3">
                {form.logo_url ? (
                  <img src={mediaSrc(form.logo_url)} alt="" className="h-12 w-12 rounded-xl border border-line object-contain p-1" />
                ) : (
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl border-2 border-dashed border-line text-slate-300">
                    <ImagePlus className="h-6 w-6" />
                  </span>
                )}
                <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" data-testid="pm-logo-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" />
                <div className="flex flex-col gap-1">
                  <button type="button" data-testid="pm-logo-upload" onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
                    className="flex items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-xs font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60">
                    {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                    {form.logo_url ? "Changer" : "Ajouter"}
                  </button>
                  {form.logo_url && (
                    <button type="button" onClick={() => set("logo_url", "")} className="text-left text-[11px] font-bold text-rose-500 hover:underline">
                      Retirer
                    </button>
                  )}
                </div>
              </div>
            </Field>
          </div>

          <Field label="Description (visible par le client)">
            <textarea data-testid="pm-desc-input" rows={2} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Ex : Effectuez votre paiement directement sur notre numéro Orange Money." className={textareaCls} />
          </Field>

          {/* LINK */}
          {form.payment_type === "link" && (
            <Field label="Lien de paiement" hint="Le client sera redirigé vers ce lien en cliquant sur « Payer maintenant ».">
              <input data-testid="pm-link-input" value={form.link} onChange={(e) => set("link", e.target.value)}
                placeholder="https://…" className={inputCls} />
            </Field>
          )}

          {/* MANUAL */}
          {form.payment_type === "manual" && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Numéro de paiement">
                <input data-testid="pm-number-input" value={form.payment_number} onChange={(e) => set("payment_number", e.target.value)}
                  placeholder="07 XX XX XX XX" className={inputCls} />
              </Field>
              <Field label="Nom du bénéficiaire">
                <input data-testid="pm-beneficiary-input" value={form.beneficiary_name} onChange={(e) => set("beneficiary_name", e.target.value)}
                  placeholder="ISMO-TECH" className={inputCls} />
              </Field>
            </div>
          )}

          {/* QR */}
          {form.payment_type === "qr" && (
            <>
              <ImageUpload
                value={form.qr_image_url}
                onChange={(url) => set("qr_image_url", url)}
                label="Image du QR Code (PNG, JPG)"
                testId="pm-qr"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Adresse de paiement / Wallet">
                  <input data-testid="pm-address-input" value={form.address} onChange={(e) => set("address", e.target.value)}
                    placeholder="0x................................" className={inputCls} />
                </Field>
                <Field label="Réseau" hint="Ex : BEP20 / BNB Smart Chain, TRC20 / Tron, ERC20…">
                  <input data-testid="pm-network-input" value={form.network} onChange={(e) => set("network", e.target.value)}
                    placeholder="BNB Smart Chain (BEP20)" className={inputCls} />
                </Field>
              </div>
              <div className="rounded-xl border border-line p-4">
                <Toggle
                  testId="pm-convert-toggle"
                  checked={form.convert_enabled}
                  onChange={(v) => set("convert_enabled", v)}
                  label="Afficher un montant converti au client"
                  hint="Convertit le total FCFA vers la devise crypto ci-dessous."
                />
                {form.convert_enabled && (
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <Field label="Devise cible">
                      <input data-testid="pm-convert-currency" value={form.convert_currency} onChange={(e) => set("convert_currency", e.target.value)}
                        placeholder="USDT" className={inputCls} />
                    </Field>
                    <Field label="Taux : 1 unité = ? FCFA" hint="Ex : 1 USDT = 600 FCFA">
                      <input data-testid="pm-convert-rate" type="number" value={form.convert_rate} onChange={(e) => set("convert_rate", e.target.value)}
                        placeholder="600" className={inputCls} />
                    </Field>
                  </div>
                )}
              </div>
            </>
          )}

          {/* INSTRUCTIONS (manual, qr, other) */}
          {form.payment_type !== "link" && (
            <Field label="Instructions (affichées au client)">
              <textarea data-testid="pm-instructions-input" rows={6} value={form.instructions}
                onChange={(e) => set("instructions", e.target.value)}
                placeholder={"1. Copiez notre numéro de paiement.\n2. Effectuez le montant exact indiqué.\n3. Conservez votre reçu.\n4. Entrez votre ID de transaction ci-dessous."}
                className={textareaCls} />
            </Field>
          )}

          {form.payment_type === "other" && (
            <Field label="Lien (optionnel)">
              <input value={form.link} onChange={(e) => set("link", e.target.value)} placeholder="https://…" className={inputCls} />
            </Field>
          )}

          {/* OPTIONS / interrupteurs */}
          <div>
            <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Options activables / désactivables</p>
            <div className="grid gap-2.5 sm:grid-cols-2" data-testid="pm-options">
              <Toggle testId="pm-active-toggle" checked={form.is_active} onChange={(v) => set("is_active", v)}
                label="Moyen de paiement actif" hint="Visible sur la page de paiement client" />
              <Toggle testId="pm-require-txid" checked={form.require_txid} onChange={(v) => set("require_txid", v)}
                label="Demander l'ID de transaction" />
              <Toggle testId="pm-require-proof" checked={form.require_proof} onChange={(v) => set("require_proof", v)}
                label="Demander une preuve de paiement" />
              {form.payment_type === "manual" && (
                <Toggle testId="pm-show-number" checked={form.show_number} onChange={(v) => set("show_number", v)}
                  label="Afficher le numéro de paiement" />
              )}
              {form.payment_type === "qr" && (
                <>
                  <Toggle testId="pm-show-qr" checked={form.show_qr} onChange={(v) => set("show_qr", v)}
                    label="Afficher le QR Code" />
                  <Toggle testId="pm-show-address" checked={form.show_address} onChange={(v) => set("show_address", v)}
                    label="Afficher l'adresse" />
                  <Toggle testId="pm-show-network" checked={form.show_network} onChange={(v) => set("show_network", v)}
                    label="Afficher le réseau" />
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-line pt-4">
            <button onClick={() => setModalOpen(false)} className="rounded-full border border-line px-5 py-2.5 text-xs font-bold text-ink hover:border-brand hover:text-brand">
              Annuler
            </button>
            <button data-testid="pm-save-btn" onClick={save} disabled={saving}
              className="flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-xs font-bold text-white hover:bg-brand/90 disabled:opacity-60">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? "Enregistrer" : "Créer le moyen"}
            </button>
          </div>
        </div>
      </AdminModal>
    </div>
  );
}
